import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { tenantsApi, uploadApi } from '@/lib/api';
import { Palette, Phone, Envelope, ArrowClockwise, UploadSimple, Trash, Cloud, ChatCircleText } from '@phosphor-icons/react';

// Helper to convert hex color to HSL values (channel numbers)
const hexToHsl = (hex) => {
  if (!hex || typeof hex !== 'string') return '217 100% 33%';
  let c = hex.replace('#', '').trim();
  if (c.length === 3) {
    c = c[0] + c[0] + c[1] + c[1] + c[2] + c[2];
  }
  if (c.length !== 6) return '217 100% 33%';
  const r = parseInt(c.substring(0, 2), 16) / 255;
  const g = parseInt(c.substring(2, 4), 16) / 255;
  const b = parseInt(c.substring(4, 6), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0, s = 0, l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
      default: break;
    }
    h /= 6;
  }
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
};

export const BrandingSettingsForm = ({ tenantConfig, setTenantConfig }) => {
  const [companyName, setCompanyName] = useState('');
  const [domain, setDomain] = useState('');
  const [brandColour, setBrandColour] = useState('#002FA7');
  const [supportPhone, setSupportPhone] = useState('');
  const [supportEmail, setSupportEmail] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [storageProvider, setStorageProvider] = useState('local');
  const [whatsappLoginEnabled, setWhatsappLoginEnabled] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingStorage, setSavingStorage] = useState(false);
  const [savingLoginMethods, setSavingLoginMethods] = useState(false);
  const [customerPortalUrl, setCustomerPortalUrl] = useState('');
  const [productPurchaseUrl, setProductPurchaseUrl] = useState('');

  useEffect(() => {
    if (tenantConfig) {
      setCompanyName(tenantConfig.company_name || '');
      setDomain(tenantConfig.domain || '');
      setBrandColour(tenantConfig.brand_colour || '#002FA7');
      setSupportPhone(tenantConfig.support_phone || '');
      setSupportEmail(tenantConfig.support_email || '');
      setLogoUrl(tenantConfig.logo_url || '');
      setStorageProvider(tenantConfig.storage_provider || 'local');
      setWhatsappLoginEnabled(tenantConfig.whatsapp_login_enabled !== false);
      setCustomerPortalUrl(tenantConfig.customer_portal_url || '');
      setProductPurchaseUrl(tenantConfig.product_purchase_url || '');
    }
  }, [tenantConfig]);

  const brandColorAccent = tenantConfig?.brand_colour || '#002FA7';

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploading(true);
    try {
      const url = await uploadApi.uploadFile(file);
      setLogoUrl(url);
      toast.success('Logo uploaded successfully! Make sure to click save changes.');
    } catch (error) {
      console.error('Logo upload failed:', error);
      toast.error('Failed to upload company logo');
    } finally {
      setUploading(false);
    }
  };

  const handleSaveBranding = async (e) => {
    e.preventDefault();
    if (!tenantConfig?.id) {
      toast.error('Tenant configuration context not loaded');
      return;
    }
    
    setSaving(true);
    try {
      const { data } = await tenantsApi.update(tenantConfig.id, {
        company_name: companyName,
        domain: domain || null,
        brand_colour: brandColour,
        support_phone: supportPhone,
        support_email: supportEmail,
        logo_url: logoUrl,
        customer_portal_url: customerPortalUrl || null,
        product_purchase_url: productPurchaseUrl || null,
      });

      // Update tenantConfig in app store
      setTenantConfig(data);

      // Inject updated HSL variables into root style sheet instantly
      const root = document.documentElement;
      const primaryHsl = hexToHsl(data.brand_colour || '#002FA7');
      root.style.setProperty('--primary', primaryHsl);
      root.style.setProperty('--ring', primaryHsl);

      toast.success('Tenant settings saved successfully!');
    } catch (error) {
      console.error('Failed to update tenant branding:', error);
      toast.error(error.response?.data?.detail || 'Failed to update tenant settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveStorage = async (e) => {
    e.preventDefault();
    if (!tenantConfig?.id) {
      toast.error('Tenant configuration context not loaded');
      return;
    }
    
    setSavingStorage(true);
    try {
      const { data } = await tenantsApi.update(tenantConfig.id, {
        storage_provider: storageProvider
      });

      // Update tenantConfig in app store
      setTenantConfig(data);
      toast.success('Storage provider configuration updated successfully!');
    } catch (error) {
      console.error('Failed to update storage settings:', error);
      toast.error(error.response?.data?.detail || 'Failed to update storage settings');
    } finally {
      setSavingStorage(false);
    }
  };

  const handleToggleWhatsappLogin = async (checked) => {
    if (!tenantConfig?.id) {
      toast.error('Tenant configuration context not loaded');
      return;
    }
    setWhatsappLoginEnabled(checked);
    setSavingLoginMethods(true);
    try {
      const { data } = await tenantsApi.update(tenantConfig.id, {
        whatsapp_login_enabled: checked
      });
      setTenantConfig(data);
      toast.success(checked ? 'WhatsApp OTP login enabled' : 'WhatsApp OTP login disabled — customers will use Google or email/password instead');
    } catch (error) {
      console.error('Failed to update login methods:', error);
      toast.error(error.response?.data?.detail || 'Failed to update login methods');
      setWhatsappLoginEnabled(!checked);
    } finally {
      setSavingLoginMethods(false);
    }
  };

  return (
    <div className="space-y-8">
      {/* Organization Branding Card */}
      <Card className="p-6 border shadow-sm rounded-lg">
        <div className="flex items-center gap-3 mb-6 pb-4 border-b border-slate-100">
          <div className="p-2 bg-primary/10 rounded-sm text-primary">
            <Palette size={24} style={{ color: brandColorAccent }} />
          </div>
          <div>
            <h2 className="text-xl font-bold text-slate-850">Organization Branding</h2>
            <p className="text-sm text-slate-500">Configure public facing company name, colors, and contacts</p>
          </div>
        </div>

        <form onSubmit={handleSaveBranding} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Company Name */}
            <div className="space-y-2">
              <Label htmlFor="companyName" className="text-xs font-bold uppercase tracking-wider text-slate-700">
                Company Name
              </Label>
              <Input
                id="companyName"
                type="text"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                placeholder="Enter company name"
                className="h-11 border-slate-350 text-slate-900 focus-ring"
                required
              />
              <p className="text-xs text-slate-500 font-medium">
                This updates the title shown on the login screens and dashboards.
              </p>
            </div>

            {/* Custom Domain */}
            <div className="space-y-2">
              <Label htmlFor="domain" className="text-xs font-bold uppercase tracking-wider text-slate-700">
                Custom Domain
              </Label>
              <Input
                id="domain"
                type="text"
                value={domain}
                onChange={(e) => setDomain(e.target.value.trim().toLowerCase())}
                placeholder="kwicc.yourcompany.com"
                className="h-11 border-slate-350 text-slate-900 focus-ring"
              />
              <p className="text-xs text-slate-500 font-medium">
                The domain this tenant is reached on. CNAME it to the platform and set up
                Cloudflare Tunnel routing first — this only tells the backend which tenant
                to load for that domain.
              </p>
            </div>

            {/* Brand Color */}
            <div className="space-y-2">
              <Label htmlFor="brandColour" className="text-xs font-bold uppercase tracking-wider text-slate-700">
                Theme brand color
              </Label>
              <div className="flex gap-3">
                <Input
                  id="brandColour"
                  type="color"
                  value={brandColour}
                  onChange={(e) => setBrandColour(e.target.value)}
                  className="w-16 h-11 p-1 border-slate-350 cursor-pointer"
                />
                <Input
                  type="text"
                  value={brandColour}
                  onChange={(e) => setBrandColour(e.target.value)}
                  placeholder="#002FA7"
                  className="h-11 border-slate-355 text-slate-900 font-mono uppercase focus-ring"
                  pattern="^#[0-9a-fA-F]{6}$"
                  required
                />
              </div>
              <p className="text-xs text-slate-500 font-medium">
                Hex code or color picker to set primary visual highlights and buttons.
              </p>
            </div>

            {/* Company Logo */}
            <div className="space-y-2 col-span-1 md:col-span-2">
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-700">
                Company Logo
              </Label>
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 p-4 border rounded-lg bg-slate-50/50">
                {logoUrl ? (
                  <div className="h-16 w-32 border bg-white rounded-sm overflow-hidden flex items-center justify-center p-2 relative group shadow-sm">
                    <img src={logoUrl} alt="Preview" className="h-full w-full object-contain" />
                    <button
                      type="button"
                      onClick={() => setLogoUrl('')}
                      className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 flex items-center justify-center text-white font-bold text-xs transition-opacity rounded-sm"
                    >
                      <Trash size={16} className="mr-1" /> Remove
                    </button>
                  </div>
                ) : (
                  <div className="h-16 w-32 border-2 border-dashed bg-white border-slate-300 rounded-sm flex items-center justify-center text-slate-400 font-bold text-xs select-none">
                    No Logo Added
                  </div>
                )}
                <div className="flex-1">
                  <input
                    type="file"
                    id="logo-file-input"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    className="hidden"
                    disabled={uploading}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => document.getElementById('logo-file-input').click()}
                    disabled={uploading}
                    className="font-bold text-xs uppercase tracking-wider h-10 border-slate-300 bg-white"
                  >
                    <UploadSimple size={16} className="mr-1.5" />
                    {uploading ? 'Uploading...' : 'Upload Logo Image'}
                  </Button>
                  <p className="text-[10px] text-slate-500 font-medium mt-1.5">
                    Accepts PNG, JPG, or SVG. Custom branding logo appears side-by-side with KWICC logo.
                  </p>
                </div>
              </div>
            </div>

            {/* Support Phone */}
            <div className="space-y-2">
              <Label htmlFor="supportPhone" className="text-xs font-bold uppercase tracking-wider text-slate-700">
                Support Contact Number
              </Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                <Input
                  id="supportPhone"
                  type="tel"
                  value={supportPhone}
                  onChange={(e) => setSupportPhone(e.target.value)}
                  placeholder="+91 12345 67890"
                  className="pl-10 h-11 border-slate-350 text-slate-900 focus-ring"
                />
              </div>
            </div>

            {/* Support Email */}
            <div className="space-y-2">
              <Label htmlFor="supportEmail" className="text-xs font-bold uppercase tracking-wider text-slate-700">
                Support Email Address
              </Label>
              <div className="relative">
                <Envelope className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                <Input
                  id="supportEmail"
                  type="email"
                  value={supportEmail}
                  onChange={(e) => setSupportEmail(e.target.value)}
                  placeholder="support@company.com"
                  className="pl-10 h-11 border-slate-350 text-slate-900 focus-ring"
                />
              </div>
            </div>
          </div>

          {/* Configurable URLs Section */}
          <div className="pt-4 border-t border-slate-100">
            <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-4">Integration URLs</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="customerPortalUrl" className="text-xs font-bold uppercase tracking-wider text-slate-700">
                  Customer Portal URL
                </Label>
                <Input
                  id="customerPortalUrl"
                  type="url"
                  value={customerPortalUrl}
                  onChange={(e) => setCustomerPortalUrl(e.target.value)}
                  placeholder="https://support.yourcompany.com"
                  className="h-11 border-slate-350 text-slate-900 focus-ring"
                />
                <p className="text-[10px] text-slate-500 font-medium">
                  Link sent in WhatsApp notifications for customers to view their tickets online.
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="productPurchaseUrl" className="text-xs font-bold uppercase tracking-wider text-slate-700">
                  Product Purchase URL
                </Label>
                <Input
                  id="productPurchaseUrl"
                  type="url"
                  value={productPurchaseUrl}
                  onChange={(e) => setProductPurchaseUrl(e.target.value)}
                  placeholder="https://shop.yourcompany.com"
                  className="h-11 border-slate-350 text-slate-900 focus-ring"
                />
                <p className="text-[10px] text-slate-500 font-medium">
                  Link shown on product cards in the customer portal for purchasing products.
                </p>
              </div>
            </div>
          </div>

          <div className="flex justify-end pt-4 border-t border-slate-100">
            <Button 
              type="submit" 
              disabled={saving}
              className="font-bold text-white shadow hover:opacity-90 px-6 py-2 h-11"
              style={{ backgroundColor: brandColorAccent }}
            >
              {saving ? (
                <>
                  <ArrowClockwise className="animate-spin mr-2" size={18} />
                  Saving changes...
                </>
              ) : (
                'Save Branding Configuration'
              )}
            </Button>
          </div>
        </form>
      </Card>

      {/* Storage Configuration Card */}
      <Card className="p-6 border shadow-sm rounded-lg">
        <div className="flex items-center gap-3 mb-6 pb-4 border-b border-slate-100">
          <div className="p-2 bg-primary/10 rounded-sm text-primary">
            <Cloud size={24} style={{ color: brandColorAccent }} />
          </div>
          <div>
            <h2 className="text-xl font-bold text-slate-850">Storage Provider Configuration</h2>
            <p className="text-sm text-slate-500">Configure where uploaded assets (logos, catalog images) are persisted</p>
          </div>
        </div>

        <form onSubmit={handleSaveStorage} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Storage Provider */}
            <div className="space-y-2 col-span-1 md:col-span-2">
              <Label htmlFor="storageProvider" className="text-xs font-bold uppercase tracking-wider text-slate-700">
                Active Storage Provider
              </Label>
              <Select value={storageProvider} onValueChange={setStorageProvider}>
                <SelectTrigger className="h-11 border-slate-350 text-slate-900 bg-white" data-testid="storage-provider-select">
                  <SelectValue placeholder="Select storage provider" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="local">Local Filesystem Storage (Host Disk)</SelectItem>
                  <SelectItem value="r2">Cloudflare R2 Object Storage (S3 API)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 font-medium">
                Choosing "Cloudflare R2" will upload new files directly to Cloudflare, serving them via time-limited secure pre-signed URLs.
              </p>
            </div>
          </div>

          <div className="flex justify-end pt-4 border-t border-slate-100">
            <Button 
              type="submit" 
              disabled={savingStorage}
              className="font-bold text-white shadow hover:opacity-90 px-6 py-2 h-11"
              style={{ backgroundColor: brandColorAccent }}
            >
              {savingStorage ? (
                <>
                  <ArrowClockwise className="animate-spin mr-2" size={18} />
                  Saving changes...
                </>
              ) : (
                'Save Storage Configuration'
              )}
            </Button>
          </div>
        </form>
      </Card>

      {/* Login Methods Card */}
      <Card className="p-6 border shadow-sm rounded-lg">
        <div className="flex items-center gap-3 mb-6 pb-4 border-b border-slate-100">
          <div className="p-2 bg-primary/10 rounded-sm text-primary">
            <ChatCircleText size={24} style={{ color: brandColorAccent }} />
          </div>
          <div>
            <h2 className="text-xl font-bold text-slate-850">Login Methods</h2>
            <p className="text-sm text-slate-500">Control which sign-in options are available to customers</p>
          </div>
        </div>

        <div className="flex items-center justify-between p-4 border rounded-lg bg-slate-50/50">
          <div className="pr-4">
            <Label htmlFor="whatsappLoginToggle" className="text-sm font-bold text-slate-800">
              WhatsApp OTP Login
            </Label>
            <p className="text-xs text-slate-500 font-medium mt-1">
              WhatsApp Cloud API charges per conversation. Turn this off to limit customers to
              Google Sign-In and email/password — both free. Existing phone-registered accounts
              can still log in via email/password.
            </p>
          </div>
          <Switch
            id="whatsappLoginToggle"
            checked={whatsappLoginEnabled}
            onCheckedChange={handleToggleWhatsappLogin}
            disabled={savingLoginMethods}
          />
        </div>
      </Card>
    </div>
  );
};
